import { addListener } from "@reduxjs/toolkit";
import { useEffect, useRef } from "react";
import { Timeline, TimelineWindow } from "vis-timeline";
import { RootState } from "@/app/appStore";
import { useAppDispatch, useAppSelector, useAppStore } from "@/app/appStore";
import { timelineOptions } from "@/pages/planogram/timeline-options";

type UseTimelineZoomProps = {
  getZoomMinMax: () => {
    zoomMax: number;
    zoomMin: number;
  };
  timelineRef: React.RefObject<Timeline>;
  options?: {
    animation: boolean;
  };
  callback?: () => void;
};

export const useTimelineZoom = ({
  timelineRef,
  getZoomMinMax,
}: UseTimelineZoomProps) => {
  const isZooming = useRef(false);
  const isFocusAnimating = useRef(false);
  const store = useAppStore();
  const dispatch = useAppDispatch();

  /**
   * Calculate window duration using exponential interpolation.
   * This ensures the duration is always positive and scales smoothly.
   *
   * @param zoomLevel - The current zoom level from 0 to 1
   * @param window - The visible timeline window
   * @param currentTime - The current time of the cursor
   * @returns [Date, Date]
   */
  const smoothScale = (
    zoomLevel: number,
    window: TimelineWindow,
    currentTime: number | null
  ) => {
    const currentWindowCenter = currentTime
      ? currentTime
      : (window.start.getTime() + window.end.getTime()) / 2;

    const { zoomMax: maxDuration, zoomMin: minDuration } = getZoomMinMax();

    const zoomFactor = Math.exp(
      Math.log(maxDuration / minDuration) * (1 - zoomLevel)
    );
    const windowDuration = minDuration * zoomFactor;

    const halfDuration = windowDuration / 2;
    const newStartDate = currentWindowCenter - halfDuration;
    const newEndDate = currentWindowCenter + halfDuration;

    return [new Date(newStartDate), new Date(newEndDate)];
  };

  /**
   * Zoom in/out visible window relative to cursor/center
   */
  const onZoomChange = (
    zoomLevel: number,
    currentTime = store.getState().player.currentTime,
    shouldAnimate?: boolean
  ) => {
    if (!timelineRef.current) return;

    const window = timelineRef.current.getWindow();

    const [newStartDate, newEndDate] = smoothScale(
      zoomLevel,
      window,
      currentTime
    );

    if (shouldAnimate || !isFocusAnimating.current) {
      isZooming.current = true;
    }

    timelineRef.current.setWindow(
      newStartDate,
      newEndDate,
      {
        animation: shouldAnimate ?? !isFocusAnimating.current,
      },
      () => {
        isZooming.current = false;
      }
    );
  };

  useEffect(() => {
    /**
     * Set initial zoom
     */
    setTimeout(() => {
      onZoomChange(store.getState().player.zoom, Date.now(), false);
    });

    /**
     * Update timeline zoom when zoom slider changes
     */
    const unsubscribe = dispatch(
      addListener({
        predicate: (_, state, prevState) => {
          return (
            (state as RootState).player.zoom !==
            (prevState as RootState).player.zoom
          );
        },
        effect: (_, listenerApi) => {
          const state = listenerApi.getState() as RootState;
          const zoomLevel = state.player.zoom;

          onZoomChange(zoomLevel);
        },
      })
    );

    return () => {
      unsubscribe();
    };
  }, []);

  const fromTime = useAppSelector((state) => state.player.videoStream.fromTime);
  const toTime = useAppSelector((state) => state.player.videoStream.toTime);
  const isLive = useAppSelector((state) => state.player.videoStream.isLive);

  useEffect(() => {
    /**
     * Update zoom max when fromTime, toTime or isLive changes
     */
    if (fromTime && toTime && !isLive) {
      const newZoomMax =
        new Date(toTime).getTime() - new Date(fromTime).getTime();

      timelineRef.current?.setOptions({
        zoomMax: newZoomMax,
      });
    } else {
      timelineRef.current?.setOptions({
        zoomMax: timelineOptions.zoomMax!,
      });
    }
  }, [fromTime, toTime, isLive, timelineRef]);
};
